"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  MessageSquare,
  Send,
  Reply,
  Check,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Comment, mockCommentService } from "../../utils/mockData";

interface CommentsProps {
  brandId: string;
  targetType: 'brand' | 'toolbox_item';
  targetId: string;
  title?: string;
}

export function Comments({ brandId, targetType, targetId, title }: CommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    loadComments();
  }, [brandId, targetType, targetId]);

  const loadComments = async () => {
    try {
      setIsLoading(true);
      const result = await mockCommentService.getComments(brandId, targetType, targetId);
      if (result.error) {
        toast.error(result.error);
      } else {
        setComments(result.comments);
      }
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      const result = await mockCommentService.addComment(
        brandId,
        targetType,
        targetId,
        newComment.trim()
      );

      if (result.error) {
        toast.error(result.error);
      } else {
        setNewComment('');
        loadComments();
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!replyContent.trim()) return;

    setIsSubmitting(true);
    try {
      const result = await mockCommentService.addComment(
        brandId,
        targetType,
        targetId,
        replyContent.trim(),
        parentId
      );

      if (result.error) {
        toast.error(result.error);
      } else {
        setReplyContent('');
        setReplyTo(null);
        loadComments();
        toast.success('Reply added');
      }
    } catch (error) {
      toast.error('Failed to add reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResolveComment = async (commentId: string) => {
    try {
      const result = await mockCommentService.resolveComment(commentId);
      if (result.error) {
        toast.error(result.error);
      } else {
        loadComments();
        toast.success('Comment resolved');
      }
    } catch (error) {
      toast.error('Failed to resolve comment');
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const commentDate = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - commentDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return commentDate.toLocaleDateString();
  };

  // Organize comments into threads
  const commentThreads = comments.filter(comment => !comment.parentId);
  const getReplies = (parentId: string) => 
    comments.filter(comment => comment.parentId === parentId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            {title || 'Comments'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            {title || 'Comments'} ({commentThreads.length})
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add new comment */}
        <div className="space-y-3">
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="min-h-[80px]"
          />
          <div className="flex justify-end">
            <Button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || isSubmitting}
              size="sm"
            >
              <Send className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </div>

        {/* Comments list */}
        {commentThreads.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No comments yet</p>
            <p className="text-sm text-gray-400">
              Start the conversation by adding the first comment
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {commentThreads.map((comment) => {
              const replies = getReplies(comment.id);
              
              return (
                <div key={comment.id} className="space-y-3">
                  {/* Main comment */}
                  <div className="flex space-x-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={comment.userAvatar} />
                      <AvatarFallback>
                        {comment.userName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-sm">{comment.userName}</p>
                          <Badge variant="outline" className="text-xs">
                            {formatTimeAgo(comment.timestamp)}
                          </Badge>
                          {comment.resolved && (
                            <Badge variant="secondary" className="text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Resolved
                            </Badge>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setReplyTo(comment.id)}
                            >
                              <Reply className="w-4 h-4 mr-2" />
                              Reply
                            </DropdownMenuItem>
                            {!comment.resolved && (
                              <DropdownMenuItem
                                onClick={() => handleResolveComment(comment.id)}
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Mark as Resolved
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  {replies.length > 0 && (
                    <div className="ml-11 space-y-3">
                      {replies.map((reply) => (
                        <div key={reply.id} className="flex space-x-3">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={reply.userAvatar} />
                            <AvatarFallback className="text-xs">
                              {reply.userName.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-sm">{reply.userName}</p>
                              <Badge variant="outline" className="text-xs">
                                {formatTimeAgo(reply.timestamp)}
                              </Badge>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-2">
                              <p className="text-sm whitespace-pre-wrap">{reply.content}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply form */}
                  {replyTo === comment.id && (
                    <div className="ml-11 space-y-2">
                      <Textarea
                        placeholder="Write a reply..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        className="min-h-[60px]"
                      />
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setReplyTo(null);
                            setReplyContent('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSubmitReply(comment.id)}
                          disabled={!replyContent.trim() || isSubmitting}
                        >
                          <Send className="w-3 h-3 mr-2" />
                          Reply
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}